// Generic .xlsx download: describe the columns, hand over the rows, get a file. Project-agnostic —
// knows nothing about what the rows mean. ExcelJS is imported lazily so the (large) library only
// loads when an export actually happens.
import type { Worksheet } from 'exceljs';

export type ExcelCellValue = string | number | Date | null;

export type ExcelColumnKind = 'text' | 'number' | 'integer' | 'date';

// How a column is summarised in the total row: the sum of its values, or the number of distinct
// non-empty values.
export type ExcelColumnTotal = 'sum' | 'countDistinct';

export interface ExcelColumn<TRow> {
  header: string;
  kind: ExcelColumnKind;
  width: number;
  value: (row: TRow) => ExcelCellValue;
  total?: ExcelColumnTotal;
}

export interface ExcelSheetOptions {
  fileName: string;
  sheetName: string;
  numberFormat: string;
  integerFormat: string;
  dateFormat: string;
  // Written in the first cell of the total row (only present when a column has a `total`).
  totalLabel?: string;
}

const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function cellFormat<TRow>(column: ExcelColumn<TRow>, options: ExcelSheetOptions): string | null {
  if (column.kind === 'number') return options.numberFormat;
  if (column.kind === 'integer') return options.integerFormat;
  if (column.kind === 'date') return options.dateFormat;
  return null;
}

type TotalCellValue = number | { formula: string; result: number };

// Formulas keep the totals live when rows are edited in Excel; the precomputed result is cached in
// the cell so the value shows before Excel recalculates. With no data rows there is no range to
// reference, so the (zero) total is written as a plain value.
function totalCell(total: ExcelColumnTotal, letter: string, values: ExcelCellValue[]): TotalCellValue {
  const nonEmpty = values.filter((value) => value !== null && value !== '');
  const result =
    total === 'sum'
      ? nonEmpty.reduce<number>((sum, value) => sum + (typeof value === 'number' ? value : 0), 0)
      : new Set(nonEmpty.map(String)).size;
  if (values.length === 0) return result;

  const range = `${letter}2:${letter}${values.length + 1}`;
  // SUMPRODUCT/COUNTIF counts distinct values in every Excel version (UNIQUE needs Excel 365);
  // the `&""` keeps blank cells from dividing by zero, and `<>""` gives them a weight of 0.
  const formula = total === 'sum' ? `SUM(${range})` : `SUMPRODUCT((${range}<>"")/COUNTIF(${range},${range}&""))`;
  return { formula, result };
}

// Appends a bold total row, separated from the data by one empty row.
function addTotalRow<TRow>(
  sheet: Worksheet,
  columns: ExcelColumn<TRow>[],
  rows: TRow[],
  label: string | undefined
): void {
  const totalRow = sheet.getRow(rows.length + 3);
  columns.forEach((column, index) => {
    if (!column.total) return;
    const values = rows.map((row) => column.value(row));
    totalRow.getCell(index + 1).value = totalCell(column.total, sheet.getColumn(index + 1).letter, values);
  });
  if (label) totalRow.getCell(1).value = label;
  totalRow.font = { bold: true };
}

// Builds the workbook bytes. Text values are written as strings (never coerced to numbers, so
// leading-zero identifiers survive); number, integer and date columns get the sheet's formats.
// When any column declares a `total`, a total row follows the data.
export async function buildExcelBuffer<TRow>(
  columns: ExcelColumn<TRow>[],
  rows: TRow[],
  options: ExcelSheetOptions
): Promise<ArrayBuffer> {
  const { Workbook } = await import('exceljs');
  const workbook = new Workbook();
  const sheet = workbook.addWorksheet(options.sheetName);

  sheet.columns = columns.map((column, index) => ({
    header: column.header,
    key: String(index),
    width: column.width
  }));
  columns.forEach((column, index) => {
    const format = cellFormat(column, options);
    if (format) sheet.getColumn(index + 1).numFmt = format;
  });

  for (const row of rows) {
    sheet.addRow(columns.map((column) => column.value(row)));
  }
  if (columns.some((column) => column.total)) {
    addTotalRow(sheet, columns, rows, options.totalLabel);
  }

  return workbook.xlsx.writeBuffer();
}

export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export async function downloadExcel<TRow>(
  columns: ExcelColumn<TRow>[],
  rows: TRow[],
  options: ExcelSheetOptions
): Promise<void> {
  const buffer = await buildExcelBuffer(columns, rows, options);
  saveBlob(new Blob([buffer], { type: XLSX_MIME_TYPE }), options.fileName);
}
